<script lang="ts" setup>
import locales from '~/assets/data/locales.json'

const { $config } = useNuxtApp()
const route = useRoute()
const isLoading = ref(true)
const companyList = ref()
const selection = '0abcdefghijklmnopqrstuvwxyz'
const selectedLetter = computed(() => route.query?.filter || '0')

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

setMeta()
getCompanyList(selectedLetter.value === '0' ? '#' : selectedLetter.value)

async function getCompanyList(letterVal: string | string[]) {
  isLoading.value = true
  const { data, error } = await useSearchApi('/companies/directory', {
    initialCache: false,
    params: {
      country: useGetSubdomain(),
      letter: letterVal,
    },
    key: 'get_company_list',
  })

  if (data.value)
    companyList.value = data.value
  isLoading.value = false
}

function setMeta() {
  const countryName = useTextCapitalize(locales.find(item => item.region === useGetSubdomain())?.label || '')
  const seoTitle = ref(`Find Jobs by Company: ${selectedLetter.value.toString().toUpperCase()} in ${countryName} | Epicareer`)
  const seoDescription = ref(`Search for jobs by ${selectedLetter.value.toString().toUpperCase()} company, and discover full time, part time, remote, and freelance opportunities. Explore additional job listings with our recommended job finder.`)
  const robotsDefault = ($config.public.ENV === 'development' || $config.public.ENV === 'staging') ? { name: 'robots', content: 'nofollow, noindex' } : { name: 'robots', content: 'index, follow' }

  if (useGetSubdomain() === 'id') {
    seoTitle.value = `Cari Loker dari Perusahaan: ${selectedLetter.value.toString().toUpperCase()} di Indonesia | Epicareer`
    seoDescription.value = `Cari pekerjaan berdasarkan perusahaan ${selectedLetter.value.toString().toUpperCase()}, dan temukan peluang kerja full time, part time, remote, dan freelance. Coba job finder kami untuk temukan loker yang sesuai untukmu.`
  }
  else if (useGetSubdomain() === 'my') {
    seoTitle.value = `Cari Kerja Mengikut Syarikat: ${selectedLetter.value.toString().toUpperCase()} di Malaysia | Epicareer`
    seoDescription.value = `Cari pekerjaan berdasarkan syarikat ${selectedLetter.value.toString().toUpperCase()}, dan temui peluang kerja sepenuh masa, sambilan, kerja dari rumah, dan freelance. Cuba job finder kami untuk menemukan jawatan yang sesuai untuk anda.`
  }

  useHead({
    title: seoTitle.value,
    meta: [
      { property: 'og:title', content: seoTitle.value },
      { name: 'twitter:title', content: seoTitle.value },
      { name: 'description', content: seoDescription.value },
      { property: 'og:description', content: seoDescription.value },
      { name: 'twitter:description', content: seoDescription.value },
      robotsDefault,
    ],
    link: [
      { rel: 'canonical', href: useSetSubdomain(useGetSubdomain()) + route.path },
    ],
  })
}
</script>

<template>
  <BaseAtomsSkeletonBounded class="!py-0 !xl:px-0 text-gray-900">
    <!-- breadcrumb -->
    <nav class="flex py-6 md:pt-10 md:pb-5 text-xs max-w-lg" aria-label="Breadcrumb">
      <ol role="list" class="flex flex-nowrap items-center overflow-hidden">
        <li class="my-1.5">
          <div class="flex items-center">
            <NuxtLink to="/jobs" class="whitespace-nowrap flex items-center text-[#5843E5]">
              Jobs
              <Icon name="mdi:chevron-right" class="w-2.5 h-2.5 mx-1" />
            </NuxtLink>
          </div>
        </li>
        <li class="my-1.5">
          Jobs by Companies
        </li>
      </ol>
    </nav>

    <p class="text-4xl font-bold font-serif">
      Jobs by Companies
    </p>
    <div class="py-6 text-xs grid grid-cols-8 sm:grid-cols-12 gap-x-3 gap-y-6 lg:space-x-4 lg:flex items-center break-all">
      <p class="col-span-2 lg:min-w-13">
        By name
      </p>
      <NuxtLink v-for="(item, idx) in selection.split('')" :key="idx" :to="`/jobs/by-company?filter=${item}`" :class="`uppercase text-[#5843E5] text-sm text-center ${selectedLetter === item ? 'font-bold' : ''}`" @click="getCompanyList(item === '0' ? '#' : item)">
        {{ item === '0' ? '#' : item }}
      </NuxtLink>
    </div>
    <div class="px-4 py-6 md:py-10 bg-[#F9FAFB] text-sm">
      <div class="font-semibold mb-8 capitalize">
        {{ selectedLetter === '0' ? '#' : selectedLetter }}
      </div>
      <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6 text-[#5843E5] font-semibold">
        <div v-for="(i) in 21" :key="i" :class="`animate-pulse w-[${Math.floor(Math.random() * (85 - 60 + 1)) + 60}%] bg-gray-200 h-5 rounded-sm`" />
      </div>
      <div v-else-if="!isLoading && !isEmpty(companyList)" class="grid grid-cols-1 md:grid-cols-3 gap-y-4 md:gap-y-8 gap-x-6 text-[#5843E5]">
        <div v-for="(item, idx) in companyList" :key="idx">
          <div v-if="!isEmpty(item.name)" class="grid gap-y-1">
            <NuxtLink :to="useGenerateSearchUrl(item.name)" class="font-semibold">
              {{ item.name }}
            </NuxtLink>
          </div>
        </div>
      </div>
      <div v-else class="py-10 text-center w-76 mx-auto">
        <Icon name="ic:baseline-business" class="w-18 h-18 mb-4 text-[#5843E5]" />
        <p class="text-lg font-bold mb-2">
          No Companies Found
        </p>
        <p class="text-sm text-gray-600">
          Sorry, currently our directory does not contain companies starting with that letter
        </p>
      </div>
    </div>
  </BaseAtomsSkeletonBounded>
</template>
